// StudioInfo — the right zone's Info tab (pen "Studio · Panel · Info"),
// slice 1: Purpose, the project's brief the assistant reads on every turn
// (#282), written on the paper (owner, 29 Oct: a click puts the caret in
// it, nothing moves, no Save; leaving keeps it); and Details, only what the
// project already holds (who it is for). The format is not a setting
// (owner, 31 Oct): it is chosen at export, so Info never shows one. Done
// when, learning objectives and requirements are not built yet, so they are
// not shown. Pure: the facts
// come in; a new purpose (or clearing it) goes out as its own intent.

export { StudioInfo };

import * as Limits from "@bilda/core/limits";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { EditableText } from "../Editor/EditableText/EditableText.js";
import { Icon } from "../Icon/Icon.js";

const StudioInfo = ({
    purpose,
    audiences,
    online,
    onDescribe,
    onClear,
}: {
    // What the project is about ("" when it says nothing).
    readonly purpose: string;
    // Who it is for, by name (none: no Details).
    readonly audiences: readonly string[];
    readonly online: boolean;
    readonly onDescribe: (purpose: string) => void;
    readonly onClear: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const keep = (text: string) => {
        const next = text.trim();
        if (next === purpose) return;
        if (next === "") onClear();
        else onDescribe(next);
    };
    return (
        <div className="bilda-studio-info">
            <section className="bilda-studio-info__section">
                <h3 className="bilda-studio-info__label">{t`Purpose`}</h3>
                {online ? (
                    <EditableText
                        value={purpose}
                        ariaLabel={t`Purpose`}
                        className="bilda-studio-info__purpose"
                        placeholder={t`Say what this project is for, and who it is for.`}
                        maxLength={Limits.LONG_TEXT_MAX}
                        onCommit={keep}
                    />
                ) : (
                    <p className="bilda-studio-info__purpose">{purpose}</p>
                )}
                <p className="bilda-studio-info__actions">
                    {t`The assistant reads this on every step`}
                </p>
            </section>
            {audiences.length === 0 ? null : (
                <section className="bilda-studio-info__section">
                    <h3 className="bilda-studio-info__label">{t`Details`}</h3>
                    <dl className="bilda-studio-info__details">
                        <dt>
                            <Icon name="person" size="sm" />
                            {t`For`}
                        </dt>
                        <dd>{audiences.join(", ")}</dd>
                    </dl>
                </section>
            )}
        </div>
    );
};
